import React, { useState, useRef, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "react-hot-toast";
import API from "../api";
import { HiExclamation } from "react-icons/hi";
import { capture } from "../instrument";

const AnswerLookup = ({ onChange, initialValue = [], questionId = null, placeholder = "Sélectionner des réponses..." }) => {
  const [inputValue, setInputValue] = useState("");
  const [tags, setTags] = useState([]);
  const [answerSuggestions, setAnswerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [options, setOptions] = useState([]);
  const inputRef = useRef(null);
  const suggestionItemsRef = useRef([]);

  // Fetch answer options when question changes
  useEffect(() => {
    setAnswerSuggestions([]);
    setOptions([]);
    setTags([]);

    if (!questionId) return;

    if (initialValue?.length > 0) setInitialValue(initialValue);
    fetchAnswerOptions();

    if (inputRef.current && initialValue?.length === 0) inputRef.current.focus();
  }, [questionId]);

  useEffect(() => {
    if (document.activeElement === inputRef.current) getSuggestions();
  }, [options, inputValue, tags]);

  // Auto-scroll to keep selected item in view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionItemsRef.current[selectedIndex]) {
      suggestionItemsRef.current[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  // Reset refs array when suggestions change
  useEffect(() => {
    suggestionItemsRef.current = suggestionItemsRef.current.slice(0, answerSuggestions.length);
  }, [answerSuggestions]);

  const setInitialValue = async (values) => {
    try {
      const response = await API.get(`/question/${questionId}`);
      const question = response.data;
      const optionValue = (option) => option.label || "";
      let tags = [];
      for (const value of values) {
        let option = question.options.find((option) => option._id === value);
        if (!option) option = { _id: value, code: value, value: "Erreur - Réponse non trouvée", inError: true };
        else option = { ...option, value: optionValue(option) };
        tags.push(option);
      }
      setTags(tags);
    } catch (error) {
      if (error?.code === "QUESTION_NOT_FOUND") return;
      capture(error);
      console.error("Error fetching answer options:", error);
    }
  };

  const fetchAnswerOptions = async () => {
    if (!questionId) return;
    try {
      const response = await API.get(`/question/${questionId}`);
      const question = response.data;
      const enrichedOptions = (question.options || []).map((opt) => ({ ...opt, value: opt.label || "" }));
      setOptions(enrichedOptions);
    } catch (error) {
      if (error?.code === "QUESTION_NOT_FOUND") return;
      capture(error);
      console.error("Error fetching answer options:", error);
      toast.error("Une erreur est survenue lors de la récupération des réponses");
    }
  };

  const getSuggestions = () => {
    let availableOptions = options.filter((option) => !tags.some((tag) => tag.code === option.code));

    if (inputValue && inputValue.length >= 2) {
      availableOptions = availableOptions.filter((option) => {
        return option.value.toLowerCase().includes(inputValue.toLowerCase()) || option.code.toLowerCase().includes(inputValue.toLowerCase());
      });
    }

    setAnswerSuggestions(availableOptions);
    setShowSuggestions(availableOptions.length > 0);
    if (availableOptions.length > 0) setSelectedIndex(0);
    else setSelectedIndex(-1);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const addTag = (option) => {
    if (option && !tags.some((tag) => tag.code === option.code)) {
      const newTags = [...tags, option];
      setTags(newTags);
      if (onChange) onChange(newTags);
    } else if (tags.some((tag) => tag.code === option.code)) {
      toast.error("Cette réponse est déjà sélectionnée");
      return;
    }

    setInputValue("");
    setSelectedIndex(-1);

    if (inputRef.current) inputRef.current.focus();
  };

  const removeTag = (tagToRemove) => {
    const newTags = tags.filter((tag) => tag.code !== tagToRemove.code);
    setTags(newTags);
    if (onChange) onChange(newTags);

    if (inputRef.current) inputRef.current.focus();
  };

  const navOpts = { enabled: showSuggestions && answerSuggestions.length > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, answerSuggestions.length - 1)), navOpts, [showSuggestions, answerSuggestions]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [showSuggestions, answerSuggestions]);
  useHotkeys("enter", () => addTag(answerSuggestions[selectedIndex >= 0 ? selectedIndex : 0]), navOpts, [showSuggestions, answerSuggestions, selectedIndex]);
  useHotkeys("escape", () => { setShowSuggestions(false); setSelectedIndex(-1); }, navOpts, [showSuggestions, answerSuggestions]);

  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={` flex flex-wrap items-center gap-1 p-2 border rounded-lg focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all duration-200 min-h-[42px] cursor-text ${
          !questionId ? "cursor-not-allowed bg-gray-100" : "bg-white"
        }`}
        onClick={() => inputRef.current.focus()}
      >
        {tags.map((tag, idx) => {
          if (tag.inError) {
            return (
              <div key={idx} className="bg-red-100 text-red-800 px-1 py-0.5 rounded-md text-sm flex items-center">
                <HiExclamation className="text-red-500" />
                <span className="ml-1 text-red-500">Erreur</span>
              </div>
            );
          }
          return (
            <div key={idx} className="bg-primary-100 text-primary-800 px-1 py-0.5 rounded-md text-sm flex items-center">
              <span className="font-medium">{tag?.code}</span>
              {tag.value && <span className="ml-1 text-xs text-gray-600">({tag.value})</span>}
              <button
                className="ml-1 text-primary-600 hover:text-primary-800"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                type="button"
                aria-label={`Remove ${tag.code}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          );
        })}
        <div className="flex items-center flex-grow">
          <input
            ref={inputRef}
            className="flex-grow outline-none min-w-[120px]"
            placeholder={!questionId ? "Veuillez d'abord sélectionner une question pour voir les réponses disponibles." : tags.length === 0 ? placeholder : ""}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={() =>
              setTimeout(() => {
                setShowSuggestions(false);
                setSelectedIndex(-1);
              }, 200)
            }
            onFocus={() => {
              if (questionId) {
                getSuggestions();
                setShowSuggestions(true);
              }
            }}
            disabled={!questionId}
          />
        </div>
      </div>

      {showSuggestions && answerSuggestions.length > 0 && (
        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto max-h-60">
          {answerSuggestions.map((suggestion, idx) => (
            <div
              ref={(el) => (suggestionItemsRef.current[idx] = el)}
              key={idx}
              className={`p-3 cursor-pointer flex justify-between items-center border-b border-gray-100 last:border-b-0 ${
                selectedIndex === idx ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
              }`}
              onClick={() => addTag(suggestion)}
            >
              <div className="flex items-center">
                <span className="font-medium">{suggestion.code}</span>
                {suggestion.value && <span className="ml-2 text-sm text-gray-500">{suggestion.value}</span>}
              </div>
              {selectedIndex === idx && <div className="text-primary-500 bg-primary-100 rounded px-2 py-1 text-xs font-medium">Enter ↵</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnswerLookup;
